import { CommonStateEnum } from "@/types/common";
import { Org, OrgAdd } from "@/types/org";
import { User, UserPermissionEnum } from "@/types/user";
import React, { FC, Fragment, useEffect, useState, useMemo, useRef, useContext, createRef, MutableRefObject } from "react";
import { OrgManagerWrap } from "./style";
import { Button, Spin, Typography, Tag, Modal, TableProps, Space, Divider, Input, Empty, Tooltip, FormInstance, Flex, App } from "antd";
import { DeleteTwoTone, PlusCircleOutlined } from '@ant-design/icons';
import CustomForm from "@/styles/Form/CustomForm";
import useOrg from "./hooks/useOrgDetail";
import { AddUser } from "../UserManager/components/AddUser";
import WMSAvatar from '@/styles/WMSAvatar';
import RemoteSelect from "@/styles/Select/RemoteSelect";
import { getUserAvatarTip, getUserName, hasPermission } from '@/utils/user';
import { userList } from "@/apis/user";
import { orgAddOrgUsers, orgDisableUser } from "@/apis/org";
import { OrgsContext } from "@/layout/HomeLayout/context/OrgsContext";

import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "@/store/index"
import { setCurOrg } from "@/store/modules/orgReducer";
import { PAGE_SIZE } from "@/utils/const";

import { useTranslation } from "react-i18next";
import profileLogout from "../Profile/utils/logout";
import errorCodes from "@/apis/error";
import { getStorage, setStorage } from "@/utils/storage";
import ModalForm from "@/styles/Form/ModalForm";
import STableLayout, { TableLayoutRef } from "@/styles/STableLayout";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";
import Permission from "@/components/Permission";
import { getTreeData } from "./utils/permissionData";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";
import SText from "@/styles/SText";

const { Title } = Typography;
const { confirm } = Modal;

interface ModalFormRef {
  modalForm: FormInstance;
}

interface OrgUserFilters {
  keyword: string;
}

const INITIAL_ORG_USER_FILTERS: OrgUserFilters = {
  keyword: "",
};

export const OrgManager: FC<{ status: CommonStateEnum }> = () => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const tableRef = useRef<TableLayoutRef | null>(null);
  // redux数据操作方法
  const orgDispatch: AppDispatch = useDispatch()
  const { curOrg: currentOrg, orgsActions } = useContext(OrgsContext)
  const currentUser: User = useSelector((state: RootState) => state.userReducer.curUser)
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const [isAdd, setIsAdd] = useState<boolean>(false);
  const [addOrgModalTitle, setAddOrgModalTitle] = useState<string>(t('org.addOrgStru'));
  // 当树形数据加载完毕 默认选中第一个组织机构-展示组织详情
  const [formRef] = useState<MutableRefObject<ModalFormRef | null>>(createRef());
  const [checkId, setCheckId] = useState<string>(currentOrg?.id ?? '')
  // 成员表格
  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<OrgUserFilters>(INITIAL_ORG_USER_FILTERS);
  const { keyword } = draftFilters;
  const [orgUserTableHeight, setOrgUserTableHeight] = useState<number>(300);
  const [isAddUserModal, setIsAddUserModal] = useState<boolean>(false)
  const [addOrgUserConfirmLoading, setAddOrgUserConfirmLoading] = useState<boolean>(false)
  const [addUserModalOpen, setAddUserModalOpen] = useState<boolean>(false)
  const [checkUserIds, setCheckUserIds] = useState<string | undefined>();
  const [userStatusModal, setUserStatusModal] = useState<User | undefined>(undefined);
  const [disableConfrimLoading, setDisableConfrimLoading] = useState<boolean>(false)

  const [fetchUserLoading, setFetchUserLoading] = useState<boolean>(false);

  // 直接使用 OrgsContext 中的数据，避免重复调用接口
  // HomeLayout 已经调用了 orgFetchs() 和 orgFetchIds()，数据已经存在 Context 中
  const contextOrgs = orgsActions[0] as Array<Org>;  // 组织列表
  const { addItem, updateItem, rmItem } = orgsActions[1];  // 从 Context 获取增删改方法

  // 调用一次 fetch 获取当前组织的最新数据（只调用一次 /wms/org/fetch）
  // 注意：useOrg 返回 [detail, loading, disLoading, { methods }]
  const [fetchedDetail, deLoading, disableLoading, { disableOrg, fetch }] = useOrg();

  // 使用 fetch 获取的最新数据，如果还没有则使用 Context 中的数据
  const detail = fetchedDetail || currentOrg;

  // 使用组织详情中的用户列表，不再调用 /org/{id}/users 接口
  const orgUserList = useMemo(() => {
    if (!detail?.users) return []
    const normalizedKeyword = appliedFilters.keyword.trim().toLowerCase();
    // 按关键词过滤
    if (!normalizedKeyword) return detail.users
    return detail.users.filter(user =>
      (user.name1 && user.name1.toLowerCase().includes(normalizedKeyword)) ||
      (user.name2 && user.name2.toLowerCase().includes(normalizedKeyword)) ||
      `${user.areaCode} ${user.mobile}`.toLowerCase().includes(normalizedKeyword)
    )
  }, [detail?.users, appliedFilters.keyword])

  const orgUserTotal = orgUserList.length
  const orgUserLoading = deLoading  // 使用 fetch 的加载状态
  const [nowOrgId, setNowOrgId] = useState<string>(currentOrg?.id ?? '');

  useEffect(() => {
    const orgId = currentOrg?.id || getStorage('curOrgId');
    replaceFilters(INITIAL_ORG_USER_FILTERS);
    tableRef.current?.resetTablePage();
    if (orgId) {
      // 调用一次 fetch 获取当前组织的最新数据
      // 这会更新 fetchedDetail，从而自动更新 detail（因为 detail = fetchedDetail || currentOrg）
      fetch(orgId);
    }
    setNowOrgId(orgId);
    setCheckId(orgId);
    // 切换组织时重置成员筛选；fetch 由当前组织 id 驱动。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, getStorage('curOrgId'), replaceFilters])

  useEffect(() => {
    if (tableWrapRef.current && detail) {
      setOrgUserTableHeight(tableWrapRef?.current.clientHeight - 240)
    }
  }, [tableWrapRef.current, detail]);

  const getHasPermission = (key: UserPermissionEnum) => {
    return hasPermission(currentUser, [UserPermissionEnum.ADMIN, key], currentOrg?.id ?? '') || hasPermission(currentUser, [UserPermissionEnum.ADMIN])
  }

  const orgUserTableColumns: TableProps<User>["columns"] = [
    {
      width: 120,
      title: t('org.avatar'),
      dataIndex: "avatar",
      key: "avatar",
      render: (_, record) =>
        <WMSAvatar uniChar={record.id} avatarId={record.avatar} creator={record} alt={getUserAvatarTip(record)} size={22} />,
      className: "table-avatar",
    },
    {
      title: "Name1",
      dataIndex: "name1",
      key: "name1",
      width: 150,
    },
    {
      title: "Name2",
      dataIndex: "name2",
      key: "name2",
      width: 150,
    },
    {
      title: t('org.phNumber'),
      dataIndex: "mobile",
      key: "mobile",
      render: (_, { areaCode, mobile, id }) => (
        <Tag key={id} color="volcano">{`${areaCode} ${mobile}`}</Tag>
      ),
    },
    {
      title: t('common.opt'),
      key: "action",
      width: 260,
      dataIndex: "status",
      hidden: !getHasPermission(UserPermissionEnum.ACT_ORG_EDIT_MEM),
      render: (_, user) => (
        <Space size="middle">
          <Permission
            treeData={getTreeData(t)}
            title={t('org.setPermissions')}
            user={user}
            orgId={currentOrg?.id || ""}
            disabled={user.id === currentUser?.id}
            key={user.id}
          />
          <Divider type="vertical" />
          <Button type="link" onClick={() => setUserStatusModal(user)} >{t('org.disable')}</Button>
        </Space>
      ),
    },
  ];

  const addOrgClick = (val: number) => {
    setIsAdd(true);
    setAddOrgModalTitle(val ? t('org.addOrgStru') : t('org.addSubOrg', { name: detail?.name }));
  }

  // 将新增用户的id参与到当前组织下
  const addUserOk = (userData: User) => {
    if (nowOrgId) {
      let userIds: string[] = []
      if (checkUserIds && checkUserIds.length > 0) {
        userIds.push(...Object.values(checkUserIds))
      }
      userIds.push(userData.id)
      setCheckUserIds(userIds as unknown as string)

      // 添加用户到组织后重新获取组织详情
      orgAddOrgUsers(nowOrgId, [userData.id])
        .then(() => fetch(nowOrgId))
        .catch((err) => {
          notification.error({ placement: 'top', message: t('org.addFailed'), description: errorCodes[err] })
        })
    }
  }

  const changeUserStatusAction = () => {
    if (userStatusModal && nowOrgId) {
      setDisableConfrimLoading(true)
      orgDisableUser(nowOrgId, [userStatusModal.id])
        .then(() => {
          // 重新获取组织详情以更新用户列表
          return fetch(nowOrgId)
        })
        .then(() => {
          setUserStatusModal(undefined)
        })
        .catch((err) => {
          notification.error({ placement: 'top', message: t('org.disblMemFailed'), description: errorCodes[err] })
        })
        .finally(() => {
          setDisableConfrimLoading(false)
        })
    }
  }

  const isDelConfirmModal = () => {
    confirm({
      title: t('org.delOrgTip1'),
      content: t('org.delOrgTip2'),
      okButtonProps: { loading: disableLoading },
      onOk() {
        if (detail) {
          // api调用删除组织
          disableOrg(detail.id).then(() => {
            // 移除组织选择下拉数据
            orgsActions[1].rmItem(detail.id)
              .then((res) => {
                if (res.length > 0) {
                  setOrgIdInfo(res[0])
                } else {
                  if (contextOrgs && contextOrgs.length > 0 && hasPermission(currentUser, [UserPermissionEnum.ADMIN])) {
                    const delIndex = contextOrgs.findIndex((item) => item.id === detail.id);
                    setOrgIdInfo(contextOrgs[delIndex + 1] || contextOrgs[0])
                  }
                  if (res.length === 0 && !hasPermission(currentUser, [UserPermissionEnum.ADMIN])) {
                    notification.warning({ placement: 'top', message: t('org.prompt'), description: t('org.userNoOrg'), duration: 5 })
                    setTimeout(() => {
                      profileLogout()
                    }, 5000);
                  }
                }
                // if (hasPermission(user, [UserPermissionEnum.ADMIN]) && contextOrgs && contextOrgs.length > 0) deleteItem(detail)
              })
          })
        }
      }
    })
  }

  const setOrgIdInfo = (orgInfo: Org) => {
    setCheckId(orgInfo.id)
    // detail 来自 Context，通过 setCurOrg 更新
    orgDispatch(setCurOrg(orgInfo))
  }

  const fetchOptions = async (page: number, body: string) => {
    setFetchUserLoading(true);
    try {
      return (await userList('list', CommonStateEnum.ACTIVE, { pagination: { offset: (page - 1) * PAGE_SIZE, take: PAGE_SIZE }, body })).body.filter((item) => {
        return !orgUserList?.some(item2 => item2.id === item.id)
      })?.map(user => (
        {
          key: user.id,
          label: getUserName(user),
          value: user.id,
          extraProps: user
        }
      ))
    } finally {
      setFetchUserLoading(false);
    }
  }

  const dropdownRender = (menu: React.ReactElement) => (
    <div>
      {fetchUserLoading && <Spin style={{ width: '100%', display: 'flex', justifyContent: 'center', }} />}
      <>
        {menu}
        <div onClick={() => setAddUserModalOpen(true)} style={{ margin: '8px 0', backgroundColor: '#f5f5f5', cursor: 'pointer' }}>
          <PlusCircleOutlined style={{ color: '#1890ff', fontSize: 16, margin: '0 4px' }} />
          <span>{t('org.createNewMem')}</span>
        </div>
      </>
    </div>
  )

  const confirmAddUsers = () => {
    setAddOrgUserConfirmLoading(true)
    if (checkUserIds && checkUserIds?.length > 0 && nowOrgId) {
      orgAddOrgUsers(nowOrgId, Object.values(checkUserIds))
        .then(() => {
          // 重新获取组织详情以更新用户列表
          return fetch(nowOrgId)
        })
        .then(() => {
          setIsAddUserModal(false)
          setCheckUserIds(undefined)
        })
        .catch((err) => {
          notification.error({ placement: 'top', message: t('org.addFailed'), description: errorCodes[err] })
        })
        .finally(() => {
          setAddOrgUserConfirmLoading(false)
        })
    } else {
      notification.warning({ placement: 'top', message: t('org.selPerson') })
      setAddOrgUserConfirmLoading(false)
    }
  }

  const onCancelAddUsers = () => {
    if (checkUserIds && checkUserIds.length > 0) {
      confirm({
        title: t('org.prompt'),
        content: t('org.cancelTip'),
        onOk() {
          restoreAddUser()
        }
      })
    } else {
      restoreAddUser()
    }
  }

  const restoreAddUser = () => {
    setIsAddUserModal(false);
    setCheckUserIds(undefined);
    setAddOrgUserConfirmLoading(false);
  }

  const inputBlurHandle = (key: 'name' | 'desc', value: string) => {
    if (detail) {
      if (detail[key] !== value) {
        const paramsBody = { [key]: value }
        if (detail.pId) paramsBody.pId = detail.pId
        orgsActions[1].updateItem(detail.id, paramsBody).then(() => {
          // Context 中的数据会自动更新，无需手动更新本地状态
          if (key === 'name') orgDispatch(setCurOrg({ ...detail, [key]: value }));
        });
        detail[key] = value;
      }
    }
  }

  const handleSearch = () => {
    commitFilters();
    tableRef.current?.resetTablePage();
  };

  const handleClearFilters = () => {
    replaceFilters(INITIAL_ORG_USER_FILTERS);
    tableRef.current?.resetTablePage();
  };

  return (
    <OrgManagerWrap>
      <div className="right">
        {deLoading && <Spin className="spin-style" />}
        {!deLoading && (
          contextOrgs && contextOrgs.length > 0 ?
            (
              detail && detail?.id ?
                <>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Title level={4}>{detail && detail.name}({t('org.orgInfo')})</Title>
                    {
                      getHasPermission(UserPermissionEnum.ACT_ORG_EDIT) &&
                      <Tooltip title={t('org.delOrg')}>
                        <DeleteTwoTone style={{ fontSize: 24, marginTop: 14 }} onClick={isDelConfirmModal} />
                      </Tooltip>
                    }
                  </div>
                  <Fragment>
                    <CustomForm<OrgAdd, Org>
                      isFormItemOperate={false}
                      key={detail.id}
                      formRef={formRef}
                      formName="org"
                      formItems={[
                        {
                          label: t('org.name'),
                          key: "name",
                          type: "input",
                          value: detail.name,
                          maxLength: 100,
                          otherProps: {
                            count: { show: true, max: 100 },
                            onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                              let nameValue = e.currentTarget.value.trim()
                              if (nameValue) {
                                if (nameValue.length <= 100) inputBlurHandle('name', nameValue)
                              } else {
                                notification.warning({ placement: 'top', message: t('org.orgNameRequired') })
                                formRef.current?.modalForm.setFieldValue('name', detail.name)
                              }
                            },
                            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') e.preventDefault() },
                            disabled: !getHasPermission(UserPermissionEnum.ACT_ORG_EDIT)
                          }
                        },
                        {
                          label: t('org.desc'),
                          key: "desc",
                          type: "textarea",
                          value: detail.desc,
                          maxLength: 1000,
                          otherProps: {
                            count: { show: true, max: 1000 },
                            autoSize: { minRows: 4, maxRows: 8 },
                            onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                              const descValue = e.currentTarget.value.trim();
                              const originDesc = detail?.desc?.trim() ?? '';
                              if (descValue.length <= 1000 && descValue !== originDesc) inputBlurHandle('desc', descValue)
                            },
                            disabled: !getHasPermission(UserPermissionEnum.ACT_ORG_EDIT)
                          },
                        },
                      ]}
                      handleCancel={() => {
                      }}
                      handleOk={() => { }}
                      okApi={(res) => {
                        // 使用 Context 中的 updateItem 方法
                        return updateItem(detail.id, res).then(() => detail as Org)
                      }}
                    />
                    <div style={{ height: '86%' }}>
                      <Flex justify="space-between" align="center" style={{ width: "100%" }}>
                        <Flex align="center" gap={20}>
                          <Title level={5} style={{ margin: 0 }}>{t('org.memList')}</Title>
                          {
                            getHasPermission(UserPermissionEnum.ACT_ORG_EDIT_MEM) && <Button type='primary' onClick={() => setIsAddUserModal(true)}>{t('org.addOrgMem')}</Button>
                          }
                        </Flex>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <ListFilterBar
                            onSearch={handleSearch}
                            onReset={handleClearFilters}
                            disabled={orgUserLoading}
                          >
                            {({ submit }) => (
                              <Input
                                placeholder={t('org.plEnterUserName')}
                                value={keyword}
                                onChange={event => updateDraftFilters({ keyword: event.target.value })}
                                onPressEnter={submit}
                                style={{ width: 200 }}
                              />
                            )}
                          </ListFilterBar>
                        </div>
                      </Flex>
                      <Modal destroyOnClose title={t('org.addOrgMem')} open={isAddUserModal} confirmLoading={addOrgUserConfirmLoading} onOk={confirmAddUsers} onCancel={onCancelAddUsers}>
                        <RemoteSelect<User>
                          mode="multiple"
                          style={{ width: '96%' }}
                          value={checkUserIds}
                          onBlurFetch={true}
                          debounceTimeout={500}
                          fetchOptions={fetchOptions}
                          placeholder={t('org.selMem')}
                          dropdownRender={dropdownRender}
                          optionRender={({ data }) => {
                            return <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <SText style={{ width: 180 }} text={data.label as string} />
                              <Tag color="blue">{data.extraProps?.mobile}</Tag>
                            </div>
                          }}
                          onChange={(value) => setCheckUserIds(value)}
                        >
                        </RemoteSelect>
                      </Modal>
                      <Flex vertical style={{ width: '100%', height: '100%' }} className="table--wrap" ref={tableWrapRef}>
                        <STableLayout
                          loading={orgUserLoading}
                          dataSource={orgUserList}
                          columns={orgUserTableColumns as ColumnsType<AnyObject>}
                          scroll={{
                            x: 'max-content',
                            y: orgUserTableHeight,
                            scrollToFirstRowOnChange: true
                          }}
                          style={{ marginTop: 10 }}
                          headerChildren={null}
                          tableLayoutRef={tableRef}
                          total={orgUserTotal}
                        />
                      </Flex>
                      <AddUser openStatus={addUserModalOpen} orgId={detail.id} onOk={addUserOk} onCancel={() => setAddUserModalOpen(false)} />
                    </div>
                  </Fragment>
                </>
                :
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('org.noOrgData')} style={{ lineHeight: 40 }} />
            )
            :
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              imageStyle={{ height: 60 }}
              description={t('org.orgListEmpty')}
              style={{ marginTop: '36vh' }}
            >
              <Button type="primary" onClick={() => addOrgClick(1)}>{t('org.addOrg')}</Button>
            </Empty>
        )
        }
        <Modal title={t('org.prompt')} open={!!userStatusModal} confirmLoading={disableConfrimLoading} onOk={changeUserStatusAction} onCancel={() => setUserStatusModal(undefined)}>
          <p>{t('org.conAction')} {userStatusModal?.state === CommonStateEnum.ACTIVE ? t('org.disable').toLowerCase() : t('org.enable').toLowerCase()} {t('org.thisMember')}?</p>
          <p>Name1: {userStatusModal?.name1 ?? ''}</p>
          <p>Name2: {userStatusModal?.name2 ?? ''}</p>
          <p>{t('member.phNumber')}: {userStatusModal?.areaCode} {userStatusModal?.mobile}</p>
        </Modal>
      </div>
      {isAdd && <ModalForm<OrgAdd, Org>
        isOpen={isAdd}
        maskClosable={false}
        modalTitle={addOrgModalTitle}
        formName={"categoryAdd"}
        formItems={[
          {
            label: t('org.name'),
            key: "name",
            value: "",
            type: "input",
            maxLength: 100,
            otherProps: {
              placeholder: t('org.enterName'),
              count: { show: true, max: 100 },
            },
            rules: [{ required: true, message: t('org.enterName') }],
          },
          {
            label: t('org.desc'),
            key: "desc",
            value: "",
            type: "textarea",
            maxLength: 1000,
            otherProps: {
              placeholder: t('org.enterDesc'),
              autoSize: { minRows: 4, maxRows: 8 },
              count: { show: true, max: 1000 },
            }
          },
        ]}
        handleCancel={() => setIsAdd(false)}
        handleOk={(res) => {
          // Context 中的数据会自动更新，无需手动更新本地状态
          setStorage('curOrgId', res.id);
          orgDispatch(setCurOrg(res));
        }}
        okApi={(info) => {
          return orgsActions[1].addItem(null, info)
        }}
      />}
    </OrgManagerWrap>
  );
}

export default OrgManager;
